import styled, { DefaultTheme } from 'styled-components';
import { Card, Button, Input, Space, Typography, Table, Switch, Progress, Radio } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const ActionIconButton = styled.div<{ iconColor?: string }>`
  align-items: center;
  border: 1px solid ${(props) => getThemeValue(props, '#434343', '#eee')};
  border-radius: 10px;
  display: flex;
  height: 30px;
  justify-content: center;
  padding: 8px;
  width: 30px;
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;
  background: ${(props) => getThemeValue(props, 'transparent', 'inherit')};

  svg {
    transition: color 0.2s;
  }

  &:hover {
    background-color: ${(props) => {
      const iconColor = props.iconColor || '#2D6CCA';
      if (iconColor === '#f5222d') {
        return getThemeValue(props, 'rgba(245, 34, 45, 0.2)', 'rgba(245, 34, 45, 0.1)');
      }
      return getThemeValue(props, 'rgba(45, 108, 202, 0.2)', 'rgba(45, 108, 202, 0.1)');
    }} !important;

    svg {
      color: ${(props) => props.iconColor || '#2D6CCA'} !important;
    }
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const Container = styled.div`
  width: 100%;
  padding: 24px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f5f5f5')};
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const Title = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 700 !important;
`;

export const Subtitle = styled(Typography.Text)`
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  display: block;
  margin-top: 8px;
`;

export const ProfileCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  margin-bottom: 24px;
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};

  .ant-card-body {
    padding: 16px;
  }
`;

export const ProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

export const ProfileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

export const ProfileDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ProfileName = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-size: 24px !important;
`;

export const ProfileUsername = styled(Typography.Text)`
  font-size: 16px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
`;

export const ProfileLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  font-size: 14px;
`;

export const SettingsCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: ${(props) => getThemeValue(props, '0 4px 12px rgba(0, 0, 0, 0.4)', '0 4px 12px rgba(0, 0, 0, 0.12)')};
  }

  .ant-card-body {
    padding: 16px;
  }
`;

export const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const CardTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const CardTitleText = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-size: 18px !important;
`;

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f9f9f9')};
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#21262d', '#e8e8e8')};
  margin-bottom: 12px;
`;

export const InfoLabel = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')} !important;
  font-weight: 500;
  min-width: 120px;
`;

export const InfoValue = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 600;
  flex: 1;
`;

export const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const StyledInput = styled(Input)`
  padding: 7px 10px !important;
  border-radius: 8px !important;
`;

export const StyledButton = styled(Button)`
  border-radius: 8px;
  height: 40px;
  font-weight: 500;
`;

export const SaveChangesButton = styled(Button)`
  background-color: #2D6CCA !important;
  color: #ffffff !important;
  font-size: 16px !important;
  border-radius: 8px !important;
  padding: 8px 10px !important;
  height: auto !important;
  
  &:hover {
    background-color: #1a4a8f !important;
    color: #ffffff !important;
  }
`;

export const EditProfileButton = styled(Button)`
  background-color: #2D6CCA !important;
  border: 0 !important;
  color: #ffffff !important;
  font-size: 16px !important;
  border-radius: 8px !important;
  padding: 6px 10px !important;
  height: auto !important;
  
  &:hover {
    background-color: #1a4a8f !important;
    border: 0 !important;
    color: #ffffff !important;
  }
  
  &:disabled {
    cursor: not-allowed !important;
  }
`;

export const CancelButton = styled(Button)`
  background-color: #ededed !important;
  border: 0 !important;
  color: #121212 !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  border-radius: 8px !important;
  padding: 8px 16px !important;
  height: auto !important;
  
  &:hover {
    background-color: #d4d4d4 !important;
    border: 0 !important;
    color: #121212 !important;
  }
`;

export const ActionButtons = styled(Space)`
  margin-top: 16px;
`;

export const StyledTable = styled(Table)`
  .ant-table {
    background: ${(props) => getThemeValue(props, '#0d1117', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }

  .ant-table-thead > tr > th {
    background: ${(props) => getThemeValue(props, '#161b22', '#fafafa')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#30363d', '#e8e8e8')};
    font-weight: 600;
  }

  .ant-table-tbody > tr {
    &:hover {
      background: ${(props) => getThemeValue(props, '#161b22', '#f5f5f5')};
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#21262d', '#e8e8e8')};
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }
`;

export const StyledSwitch = styled(Switch)`
  &.ant-switch-checked {
    background-color: #1890ff;
  }
`;

export const StyledProgress = styled(Progress)`
  .ant-progress-text {
    color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
  }
`;

export const PreferenceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f9f9f9')};
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#21262d', '#e8e8e8')};
  margin-bottom: 12px;
`;

export const PreferenceInfo = styled.div`
  flex: 1;
`;

export const PreferenceLabel = styled(Typography.Text)`
  font-size: 14px;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')} !important;
  font-weight: 600;
  display: block;
  margin-bottom: 4px;
`;

export const PreferenceDescription = styled.p`
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  line-height: normal;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
`;

export const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const TwoStepMethodOption = styled(Radio)`
  display: flex !important;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  .ant-radio + span {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }
`;

export const TwoStepInputWrap = styled.div`
  max-width: 320px;
`;

export const TwoStepEnableBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: ${(props) => getThemeValue(props, '#0d1117', '#f9f9f9')};
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#21262d', '#e8e8e8')};
`;

export const TwoStepSwitchWrap = styled.div`
  width: fit-content;
`;

export const TwoStepEnableTitle = styled.p`
  margin: 0;
  font-weight: 600;
  color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
`;

export const TwoStepEnableDescription = styled.p`
  margin: 0;
  font-size: 12px;
  color: ${(props) => getThemeValue(props, '#8b949e', '#666')};
  line-height: normal;
`;

export const EditingBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 24px;
  background: #2D6CCA;
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  z-index: 1;
`;

export const EditingCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid #2D6CCA;
  background: ${(props) => getThemeValue(props, '#161b22', '#ffffff')};
  margin-bottom: 24px;
  box-shadow: ${(props) => getThemeValue(props, '0 2px 8px rgba(0, 0, 0, 0.3)', '0 2px 8px rgba(0, 0, 0, 0.08)')};
  position: relative;

  .ant-card-body {
    padding: 16px;
  }
`;

